import { useCallback, useEffect, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type { NotificationsState } from '../api/monitor';

/**
 * Cadência do contador do sino.
 *
 * 30 s é o atraso máximo entre o fato acontecer no servidor e o sino acender.
 * Mais curto do que isso só faria sentido com push — e o que se ganharia em
 * segundos não paga um canal aberto por usuário só para dizer "nada novo".
 */
const REFRESH_MS = 30_000;

export interface NotificationsController {
  state: NotificationsState | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Marca como vistas as que já estavam na tela — o que abrir o sino faz. */
  markSeen: () => Promise<void>;
  /** Marca uma só, no clique do item. */
  markRead: (id: number) => Promise<void>;
}

/**
 * Central de notificações.
 *
 * Vive no App, e não na tela: o sino fica no cabeçalho de todas elas, e é o
 * mesmo estado que a tela de Notificações lista — dois controllers dariam dois
 * contadores que discordam entre si.
 */
export function useNotifications(): NotificationsController {
  const [state, setState] = useState<NotificationsState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await monitorApi.notifications());
      setError(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Com a aba escondida não adianta gastar request.
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void reload();
    }, REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [reload]);

  /**
   * O corte é a notificação mais recente que o usuário tem na tela: o aviso que
   * chegar entre a abertura do balão e o request continua não lido, em vez de
   * nascer marcado sem nunca ter aparecido.
   */
  const markSeen = useCallback(async () => {
    const latest = state?.notifications[0];

    if (!latest || state?.unread === 0) return;

    setState(await monitorApi.readNotifications(latest.id));
  }, [state]);

  const markRead = useCallback(async (id: number) => {
    setState(await monitorApi.readNotification(id));
  }, []);

  return { state, loading, error, reload, markSeen, markRead };
}
